import React, { useState } from 'react';
import { X, Globe, Copy, Check, ChevronDown, Lock, ExternalLink } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { copyToClipboard } from '../utils/clipboard';

interface ShareModalProps {
  onClose: () => void;
  onPreviewPublicLink: () => void;
}

export default function ShareModal({ onClose, onPreviewPublicLink }: ShareModalProps) {
  const [permission, setPermission] = useState<'view' | 'edit'>('view');
  const [isPublic, setIsPublic] = useState(true);
  const [copied, setCopied] = useState(false);
  const { t } = useLanguage();

  const shareUrl = "https://minutes.ai/s/xf9-2k9-retro";

  const handleCopy = () => {
    copyToClipboard(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">{t.share.title}</h2>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-full transition-colors">
                <X size={20} />
            </button>
        </div>

        <div className="p-8 space-y-8">
            
            {/* Toggle Section */}
            <div className="flex items-start justify-between">
                <div className="flex gap-4">
                    <div className={`mt-1 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isPublic ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                        {isPublic ? <Globe size={20} /> : <Lock size={20} />}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900">{t.share.publicAccess}</h3>
                        <p className="text-sm text-gray-500 mt-1 max-w-[240px]">
                            {isPublic 
                                ? t.share.publicDesc 
                                : t.share.privateDesc}
                        </p>
                    </div>
                </div>
                
                {/* Switch */}
                <button 
                    onClick={() => setIsPublic(!isPublic)}
                    className={`w-12 h-7 rounded-full p-1 transition-colors duration-300 ${isPublic ? 'bg-black' : 'bg-gray-200'}`}
                >
                    <div className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${isPublic ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </button>
            </div>

            {/* Link Generation Section (Conditional) */}
            <div className={`space-y-4 transition-all duration-300 ${isPublic ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                
                <div className="flex items-center gap-3">
                    <div className="flex-1 h-12 flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-600 overflow-hidden relative group">
                        <span className="truncate pr-8">{shareUrl}</span>
                        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-gray-50 to-transparent"></div>
                    </div>
                    
                    {/* Permission Dropdown */}
                    <div className="relative shrink-0">
                        <select 
                            value={permission}
                            onChange={(e) => setPermission(e.target.value as any)}
                            className="appearance-none h-12 pl-4 pr-10 bg-white border border-gray-200 rounded-xl text-sm font-medium hover:border-gray-300 focus:border-black focus:ring-0 outline-none cursor-pointer"
                        >
                            <option value="view">{t.share.canView}</option>
                            <option value="edit">{t.share.canEdit}</option>
                        </select>
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                <div className="flex gap-3">
                    <button 
                        onClick={handleCopy}
                        className="flex-1 h-11 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                        {copied ? t.share.copied : t.share.copyLink}
                    </button>
                    
                    {/* Simulator Button */}
                    <button 
                        onClick={onPreviewPublicLink}
                        className="h-11 px-4 text-gray-600 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 hover:text-black transition-colors flex items-center gap-2"
                        title={t.share.previewTooltip}
                    >
                        <ExternalLink size={16} />
                        {t.share.preview}
                    </button>
                </div>
            </div>
            
            <div className="pt-4 border-t border-gray-100">
                <p className="text-xs text-center text-gray-400">
                    {t.share.disclaimer}
                </p>
            </div>

        </div>
      </div>
    </div>
  );
}